import { useEffect, useRef, useCallback } from 'react';
import type { Photo } from '../types';

interface LightboxProps {
  photos: Photo[];
  currentIndex: number | null;
  onClose: () => void;
  onNavigate: (index: number | null) => void;
}

export function Lightbox({ photos, currentIndex, onClose, onNavigate }: LightboxProps) {
  if (currentIndex === null || photos.length === 0) return null;

  const photo = photos[currentIndex];
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === photos.length - 1;
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    lastFocusedRef.current = document.activeElement as HTMLElement;
    containerRef.current?.focus();
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      lastFocusedRef.current?.focus();
    };
  }, [currentIndex]);

  const handleKeyDown = useCallback((e: globalThis.KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
    else if (e.key === 'ArrowLeft' && !isFirst) onNavigate(currentIndex - 1);
    else if (e.key === 'ArrowRight' && !isLast) onNavigate(currentIndex + 1);
  }, [currentIndex, isFirst, isLast, onClose, onNavigate]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown as EventListener);
    return () => document.removeEventListener('keydown', handleKeyDown as EventListener);
  }, [handleKeyDown]);

  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  const handleImageClick = useCallback(() => {
    if (!isLast) onNavigate(currentIndex + 1);
  }, [currentIndex, isLast, onNavigate]);

  const handleImageLoad = useCallback(() => {
    // Image loaded
  }, []);

  const handleImageError = useCallback(() => {
    // Image failed to load
  }, []);

  const prevPhoto = !isFirst ? photos[currentIndex - 1] : null;
  const nextPhoto = !isLast ? photos[currentIndex + 1] : null;

  return (
    <div
      ref={containerRef}
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`Vollbildansicht: ${photo.filename}`}
      onClick={handleBackdropClick}
      tabIndex={-1}
    >
      <div className="lightbox-backdrop" aria-hidden="true"></div>

      <div className="lightbox-content">
        {prevPhoto && (
          <button
            className="lightbox-nav lightbox-nav--prev"
            onClick={() => onNavigate(currentIndex - 1)}
            aria-label="Vorheriges Foto"
            tabIndex={-1}
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
        )}

        <div className="lightbox-image-wrapper" onClick={handleImageClick}>
          {photo.mimeType.startsWith('video/') && photo.canDisplay ? (
            <video
              src={`/api/photos/${photo.id}`}
              className="lightbox-image"
              controls
              playsInline
              preload="metadata"
              onClick={(e) => e.stopPropagation()}
            />
          ) : photo.canDisplay ? (
            <img
              ref={imgRef}
              src={`/api/photos/${photo.id}`}
              alt={photo.filename}
              className="lightbox-image"
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
          ) : (
            <div className="lightbox-placeholder" aria-hidden="true">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
              <p>Vorschau nicht verfügbar</p>
              <p className="lightbox-placeholder-hint">
                <a href={`/api/photos/${photo.id}`} download={photo.filename} target="_blank" rel="noopener noreferrer">
                  Originaldatei herunterladen
                </a>
              </p>
            </div>
          )}

          <div className="lightbox-counter" aria-hidden="true">
            {currentIndex + 1} / {photos.length}
          </div>
        </div>

        {nextPhoto && (
          <button
            className="lightbox-nav lightbox-nav--next"
            onClick={() => onNavigate(currentIndex + 1)}
            aria-label="Nächstes Foto"
            tabIndex={-1}
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        )}

        <button
          className="lightbox-close"
          onClick={onClose}
          aria-label="Vollbildansicht schließen"
          tabIndex={-1}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <div className="lightbox-info">
        <h2 className="lightbox-filename">{photo.filename}</h2>
        <div className="lightbox-meta">
          {photo.displayName && (
            <span className="lightbox-uploader">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              Hochgeladen von {photo.displayName}
            </span>
          )}
          <time dateTime={new Date(photo.uploadedAt).toISOString()}>
            {new Date(photo.uploadedAt).toLocaleString('de-DE', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </time>
          <span className="lightbox-size">
            {photo.fileSize > 1024 * 1024
              ? (photo.fileSize / (1024 * 1024)).toFixed(1) + ' MB'
              : (photo.fileSize / 1024).toFixed(0) + ' KB'}
          </span>
        </div>
        <div className="lightbox-actions">
          <a
            href={`/api/photos/${photo.id}`}
            download={photo.filename}
            className="btn btn-secondary btn-sm"
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Original herunterladen
          </a>
        </div>
      </div>
    </div>
  );
}